import type { UploadedFileRef, UploadLinkInfo, VendorUploadCreateResult } from '@peles/shared';

/**
 * Public (no-auth) upload-link API — `/uploads/:token`, for a vendor, a client
 * or anybody the office asked for files. Uses plain fetch — the authenticated
 * apiClient would redirect to /login on 401 and attach producer tokens, neither
 * of which applies to a tokenized link. (`/vendor-uploads/...`, the paths older
 * pages called, are aliases on the server.)
 */

const BASE = '/api/v1/uploads';

/**
 * A refusal, with the status it came with — the page says different things for
 * a link already used (409) and one that never existed (404), and a message
 * alone cannot tell them apart.
 */
export class UploadLinkError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** Files of a submission the server no longer holds — to be uploaded again. */
    readonly missingFileIds: string[] = [],
  ) {
    super(message);
    this.name = 'UploadLinkError';
  }
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let message = 'Request failed';
    let missingFileIds: string[] = [];
    try {
      const body = (await res.json()) as {
        message?: string;
        error?: { message?: string };
        missingFileIds?: unknown;
      };
      message = body.error?.message ?? body.message ?? message;
      if (Array.isArray(body.missingFileIds)) {
        missingFileIds = body.missingFileIds.filter((id): id is string => typeof id === 'string');
      }
    } catch {
      // keep default message
    }
    throw new UploadLinkError(message, res.status, missingFileIds);
  }
  return (await res.json()) as T;
}

function linkPath(token: string): string {
  return `${BASE}/${encodeURIComponent(token)}`;
}

export async function getUploadLinkInfo(token: string): Promise<UploadLinkInfo> {
  const res = await fetch(linkPath(token));
  return handle<UploadLinkInfo>(res);
}

/**
 * Where to PUT one file's bytes, and the handle to send back afterwards —
 * asked once per file.
 *
 * ⚠️ **One of `s3Key` and `fileId`, and this page never chooses.** A link asked
 * for from a conversation presigns through the file library so the file becomes
 * a real library file the thread can carry; every other link presigns into the
 * request's own prefix. Which one is a fact about the link, decided on the
 * server — this echoes back whatever it was handed, and the server checks it
 * against the request either way.
 */
export async function getUploadUrl(
  token: string,
  fileName: string,
  mimeType: string,
): Promise<{ uploadUrl: string; s3Key?: string; fileId?: string }> {
  const res = await fetch(`${linkPath(token)}/upload-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName, mimeType }),
  });
  return handle<{ uploadUrl: string; s3Key?: string; fileId?: string }>(res);
}

/**
 * Finish the submission: every file at once. The link is single-use, so this
 * is the one call that spends it — one submission carrying several files.
 */
export async function finalizeUpload(
  token: string,
  files: UploadedFileRef[],
): Promise<VendorUploadCreateResult> {
  const res = await fetch(linkPath(token), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ files }),
  });
  return handle<VendorUploadCreateResult>(res);
}
